import React, { useEffect, useState } from "react";
import MasterLayout from "../Sections/MasterLayout";
import Loader from "./Loader";
import { Plus, Pencil, Trash2 } from "lucide-react";
import DeleteModal from "../Sections/DeleteModal";
import { useNavigate } from "react-router-dom";
import {
  get_all_collections,
  delete_collection,
  server_post_json,
} from "../../ServiceConnection/serviceconnection";
import toast from "react-hot-toast";

export default function Collections() {
  const navigate = useNavigate();

  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [filterType, setFilterType] = useState("all");

  // delete modal
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [selectedCollectionId, setSelectedCollectionId] = useState(null);

  /* ================= FETCH COLLECTIONS ================= */
  const master_get_data = async () => {
    try {
      setLoading(true);

      const payload = { fast: true };
      if (filterType !== "all") {
        payload.type = filterType;
      }

      const response = await server_post_json(
        get_all_collections,
        payload
      );

      setCollections(response.data?.data || []);
    } catch (err) {
      console.error(err);
      setCollections([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    master_get_data();
  }, [filterType]);

  /* ================= SEARCH FILTER ================= */
  const filtered = collections.filter(
    (c) =>
      c.title?.toLowerCase().includes(search.toLowerCase()) ||
      c.slug?.toLowerCase().includes(search.toLowerCase())
  );

  /* ================= DELETE ================= */
  const handleDeleteCollection = async () => {
    try {
      setLoading(true);

      await server_post_json(delete_collection, {
        collection_id: selectedCollectionId,
      });

      toast.success("Collection deleted successfully");
      setDeleteOpen(false);
      setSelectedCollectionId(null);
      master_get_data();
    } catch (err) {
      console.error(err);
      toast.error("Failed to delete collection");
    } finally {
      setLoading(false);
    }
  };

  return (
    <MasterLayout>
      {loading && <Loader />}

      <div className="px-3 sm:px-4 py-6">
        {/* HEADER */}
        <div className="flex items-center justify-between mb-5">
          <h2 className="text-lg sm:text-2xl font-bold text-gray-800">
            Collections
          </h2>

          <button
            onClick={() => navigate("/add-collection")}
            className="px-6 py-2.5 bg-[var(--primary-color)] text-white rounded-lg hover:bg-[var(--primary-hover)] text-sm font-medium"
          >
            Add collection
          </button>
        </div>

        {/* SEARCH + FILTER */}
        <div className="bg-white border rounded-lg mb-4 p-3 flex flex-col sm:flex-row gap-3">
          <input
            type="text"
            placeholder="Search by title or slug"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full sm:w-64 px-3 py-2 border rounded-md text-sm"
          />

          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-3 py-2 border rounded-md text-sm"
          >
            <option value="all">All</option>
            <option value="collection">Collection</option>
            <option value="category">Category</option>
          </select>
        </div>

        {/* TABLE */}
        <div className="bg-white border rounded-xl overflow-x-auto">
          <table className="w-full min-w-[1000px]">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs text-gray-500">
                  #
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-500">
                  Title
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-500">
                  Slug
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-500">
                  Type
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-500">
                  Products
                </th>
                <th className="px-4 py-3 text-left text-xs text-gray-500">
                  Conditions
                </th>
                <th className="px-4 py-3 text-right text-xs text-gray-500">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y">
              {filtered.map((c, index) => (
                <tr key={c.collection_id} className="hover:bg-gray-50">
                  {/* INDEX */}
                  <td className="px-4 py-4 text-sm text-gray-500">
                    {index + 1}
                  </td>

                  {/* TITLE */}
                  <td className="px-4 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-12 h-12 rounded-lg border bg-gray-100 overflow-hidden flex items-center justify-center">
                        {c.imageFull ? (
                          <img
                            src={c.imageFull.replace(
                              "/upload/",
                              "/upload/q_auto,f_auto,w_120/"
                            )}
                            alt={c.title}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <Plus className="w-6 h-6 text-gray-400" />
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-900">
                        {c.title}
                      </span>
                    </div>
                  </td>

                  {/* SLUG */}
                  <td className="px-4 py-4 text-xs text-gray-600 break-all">
                    /collections/{c.slug}
                  </td>

                  {/* TYPE */}
                  <td className="px-4 py-4 text-xs text-gray-600">
                    {c.type}
                  </td>

                  {/* PRODUCTS */}
                  <td className="px-4 py-4 text-sm text-gray-700">
                    {c.product_count || 0}
                  </td>

                  {/* CONDITIONS */}
                  <td className="px-4 py-4 text-sm text-gray-600">
                    {c.conditions?.length ? (
                      <ul className="space-y-1">
                        {c.conditions.map((cond, i) => (
                          <li key={i} className="text-xs">
                            {cond.field} {cond.operator} {cond.value}
                          </li>
                        ))}
                      </ul>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>

                  {/* ACTIONS */}
                  <td className="px-4 py-4 text-right">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() =>
                          navigate(`/edit_collection/${c.collection_id}`)
                        }
                        className="p-1.5 rounded hover:bg-gray-100"
                      >
                        <Pencil className="w-4 h-4 text-gray-600" />
                      </button>

                      <button
                        onClick={() => {
                          setSelectedCollectionId(c.collection_id);
                          setDeleteOpen(true);
                        }}
                        className="p-1.5 rounded hover:bg-red-50"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}

              {filtered.length === 0 && (
                <tr>
                  <td
                    colSpan="7"
                    className="px-4 py-10 text-center text-sm text-gray-500"
                  >
                    No collections found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* DELETE MODAL */}
      <DeleteModal
        open={deleteOpen}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteCollection}
        title="Delete collection?"
        subtitle="This collection will be permanently removed."
      />
    </MasterLayout>
  );
}
